import {Router} from '@angular/router';
import {isEmpty, isNil, isObject} from 'lodash';
import {BehaviorSubject, from, Observable, of, Subscription} from 'rxjs';
import {concatMap, tap} from 'rxjs/operators';
import {LocalStorageService} from 'src/app/shared/services/local-storage.service';
import {Injectable} from "@angular/core";
import { LoginBackendResponse } from '../models/login-backend.model';
import { BackendService, BackendResponse } from './backend-service';
import { AuthenticationInfo, LoginResponse } from '../models';
import { EApplicationAccess } from '../enums';
import { TokenStorageService } from './token-storage.service';

@Injectable({providedIn: 'root'})
export abstract class BaseAuthService {

    private _hasAuthenticated: BehaviorSubject<boolean>;

    public get versionType() {
        return +localStorage.getItem(LocalStorageService.key.version);
    }

    protected constructor(
        protected _http: BackendService,
        protected _tokenStorageService: TokenStorageService,
        protected _router: Router,
    ) {
        this._hasAuthenticated = new BehaviorSubject<boolean>(this.hasToken());
    }

    public refreshToken(refreshToken: string): Observable<BackendResponse<AuthenticationInfo>> {
        return this._http.post<AuthenticationInfo>('refreshToken', {token: refreshToken});
    }

    public signOutRemotely(sessionId?: string): Observable<BackendResponse<boolean>> {
        return this._http.get<boolean>('signOutRemotely', [], sessionId ? [{
            name: 'sessionId',
            value: sessionId
        }] : []);
    }

    public getBearer(token: string) {
        const BEARER = 'Bearer';
        return token.includes(BEARER) ? token : `${BEARER} ${token}`;
    }

    public login(args: {
        twoFactor: boolean,
        token?: string,
        userId?: string,
        username?: string,
        password?: string,
        remember?: boolean
    }) {

        const service$ = args.twoFactor ?
            this._http.post<LoginBackendResponse>('loginTwoFactor', args)
            : this._http.post<LoginBackendResponse>('login', args);

        return service$.pipe(
            tap(response => this.storeAuthenticationData({
                user: response.data.user,
                tokenInfo: {...response.data.tokenInfo, remember: args.remember}
            }))
        );
    }

    public storeAuthenticationData({user, tokenInfo}: { user?: any, tokenInfo: LoginResponse }) {
        if (isObject(user)) this._tokenStorageService.saveUser(user);
        if (isObject(tokenInfo)) this._tokenStorageService.saveToken(tokenInfo);
    }

    public logout({notification, redirect}: any): Subscription;
    public logout(): Subscription;
    public logout({notification = undefined, redirect = true} = {}): Subscription {
        this.setAuthenticationState(EApplicationAccess.Denied);

        const cleanKeys = () => this._tokenStorageService.clearStorageKeys({
                keysToOmit: [
                    LocalStorageService.key.version,
                    LocalStorageService.key.darkMode,
                    LocalStorageService.key.scheduleJobs,
                ]
            })
        ;
        const returnUrl = this._router.routerState.snapshot.url;
        return redirect ?
            this.logoutServer()
                .pipe(concatMap(() => {
                    cleanKeys();
                    return this.redirectToLoginPage(returnUrl);
                }))
                .subscribe()
            : this.logoutServer().subscribe(() => cleanKeys());
    }

    public logoutServer(): Observable<any> {
        return this._hasAuthenticated.value ? this._http.put('logout', {}) : of({});
    }

    public forgotPass(username: string): Observable<any> {
        return this._http.post('forgotPassword', {'username': username});
    }

    public setAuthenticationState(state: EApplicationAccess) {
        const hasAuthenticated = state === EApplicationAccess.Allow;
        this._hasAuthenticated.next(hasAuthenticated);
    }

    public isLoggedIn({getRawValue}: { getRawValue: boolean }): boolean;
    public isLoggedIn(): Observable<boolean>;
    public isLoggedIn({getRawValue} = {getRawValue: false}) {
        const hasToken = this.hasToken();
        if (!hasToken) {
            return getRawValue ? false : this._hasAuthenticated.asObservable();
        }
        return getRawValue ? this._hasAuthenticated.getValue() : this._hasAuthenticated.asObservable();
    }

    public redirectToLoginPage(returnUrl?: string): Observable<boolean> {

        if (returnUrl !== '/login') {
            return from(this._router.navigate(['login'], !returnUrl ? {} : {queryParams: {returnUrl}}));
        }

        return from(this._router.navigate(['/']));
    }

    public hasToken() {
        const token = this._tokenStorageService.getToken();
        return !isNil(token) && !isEmpty(token);
    }
}
